/**
 * Assessment Engine
 *
 * Generates deterministic AI readiness scores from tech stack input.
 * Pure functions -- same inputs always produce same outputs.
 */

import type { TechStackInput } from "./techStacks";
import type { Dimension, DimensionScore, OverallScore, ScoringFactor } from "./scoring";
import { scoreToGrade, clampScore, weightedAverage, DIMENSION_LABELS } from "./scoring";

/** Hash a string to a deterministic number 0-1 */
function hashSeed(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h % 1000) / 1000;
}

/** Languages with strong AI tooling support */
const HIGH_AI_LANGS = new Set([
  "TypeScript",
  "JavaScript",
  "Python",
  "Java",
  "Go",
  "C#",
  "Kotlin",
]);

/** Frameworks with mature AI integration patterns */
const HIGH_AI_FRAMEWORKS = new Set([
  "React",
  "Next.js",
  "Django",
  "FastAPI",
  "Spring Boot",
  "Express",
  "NestJS",
  "LangChain",
]);

function scoreDimension(
  dimension: Dimension,
  input: TechStackInput,
): DimensionScore {
  const factors: ScoringFactor[] = [];
  let base = 50; // Start at midpoint

  const hasHighAILang = input.languages.some((l) => HIGH_AI_LANGS.has(l));
  const hasHighAIFramework = input.frameworks.some((f) =>
    HIGH_AI_FRAMEWORKS.has(f),
  );
  const hasPython = input.languages.includes("Python");
  const teamMultiplier =
    input.team.aiExperience === "mature"
      ? 1.2
      : input.team.aiExperience === "some-production"
        ? 1.1
        : input.team.aiExperience === "experimenting"
          ? 1.0
          : 0.85;

  // Universal factors
  if (hasHighAILang) {
    factors.push({
      name: "AI-friendly language",
      contribution: 10,
      reason: `${input.languages.filter((l) => HIGH_AI_LANGS.has(l)).join(", ")} have excellent AI tooling support`,
    });
    base += 10;
  }

  if (input.team.testCoverage >= 60) {
    factors.push({
      name: "Good test coverage",
      contribution: 5,
      reason: `${input.team.testCoverage}% test coverage enables safer AI-assisted changes`,
    });
    base += 5;
  }

  // Dimension-specific scoring
  switch (dimension) {
    case "code-generation":
      if (hasHighAIFramework) {
        factors.push({
          name: "AI-ready framework",
          contribution: 15,
          reason:
            "Strong code completion and generation models exist for your frameworks",
        });
        base += 15;
      }
      if (input.team.size >= 5) {
        factors.push({
          name: "Team scale",
          contribution: 8,
          reason:
            "Larger teams benefit more from AI code generation (multiplied productivity)",
        });
        base += 8;
      }
      if (input.team.deployFrequency === "daily") {
        factors.push({
          name: "High deploy frequency",
          contribution: 5,
          reason: "Frequent deploys indicate CI/CD maturity for AI integration",
        });
        base += 5;
      }
      break;

    case "testing":
      if (input.team.testCoverage < 40) {
        factors.push({
          name: "Low test coverage opportunity",
          contribution: 12,
          reason:
            "AI test generation can dramatically improve coverage from current low baseline",
        });
        base += 12;
      }
      if (hasHighAIFramework) {
        factors.push({
          name: "Framework test support",
          contribution: 10,
          reason:
            "AI models can generate high-quality tests for your framework",
        });
        base += 10;
      }
      break;

    case "data-pipeline":
      if (hasPython) {
        factors.push({
          name: "Python for data",
          contribution: 15,
          reason:
            "Python is the dominant language for AI/ML data pipeline integration",
        });
        base += 15;
      }
      if (
        input.databases.some((db) =>
          ["PostgreSQL", "ClickHouse", "Elasticsearch"].includes(db),
        )
      ) {
        factors.push({
          name: "Analytics-ready databases",
          contribution: 10,
          reason:
            "Your database choices support AI-enhanced analytics and querying",
        });
        base += 10;
      }
      break;

    case "devops":
      if (input.infrastructure.cicd) {
        factors.push({
          name: "CI/CD in place",
          contribution: 12,
          reason: "Existing CI/CD enables AI-powered deployment analysis and optimization",
        });
        base += 12;
      }
      if (input.infrastructure.containerization) {
        factors.push({
          name: "Containerized",
          contribution: 8,
          reason: "Containers simplify deploying AI model serving infrastructure",
        });
        base += 8;
      }
      if (input.infrastructure.monitoring) {
        factors.push({
          name: "Monitoring in place",
          contribution: 7,
          reason: "Existing monitoring enables AI-powered anomaly detection",
        });
        base += 7;
      }
      break;

    case "documentation":
      if (input.team.size >= 3) {
        factors.push({
          name: "Team size drives doc need",
          contribution: 10,
          reason:
            "Teams of 3+ benefit significantly from AI-generated documentation",
        });
        base += 10;
      }
      if (hasHighAILang) {
        factors.push({
          name: "Type system for docs",
          contribution: 8,
          reason: "Typed languages enable richer AI-generated documentation",
        });
        base += 8;
      }
      break;

    case "customer-facing":
      if (
        input.frameworks.some((f) =>
          ["React", "Next.js", "Vue", "Angular", "Svelte"].includes(f),
        )
      ) {
        factors.push({
          name: "Modern frontend",
          contribution: 12,
          reason:
            "Your frontend framework supports AI chat, search, and recommendation UIs",
        });
        base += 12;
      }
      if (
        input.infrastructure.cloud === "AWS" ||
        input.infrastructure.cloud === "Google Cloud" ||
        input.infrastructure.cloud === "Cloudflare"
      ) {
        factors.push({
          name: "AI-friendly cloud",
          contribution: 8,
          reason:
            "Your cloud provider offers managed AI/ML services for customer-facing features",
        });
        base += 8;
      }
      break;
  }

  // Apply team maturity multiplier
  const rawScore = base * teamMultiplier;

  // Add a deterministic pseudo-random variation so scores aren't too neat
  const seed = hashSeed(dimension + JSON.stringify(input.languages));
  const variation = (seed - 0.5) * 6; // +/- 3

  const finalScore = clampScore(rawScore + variation);

  return {
    dimension,
    score: finalScore,
    grade: scoreToGrade(finalScore),
    rationale: `${DIMENSION_LABELS[dimension]}: Score based on ${factors.length} contributing factors`,
    factors,
  };
}

export function runAssessment(input: TechStackInput): OverallScore {
  const dimensions: Dimension[] = [
    "code-generation",
    "testing",
    "data-pipeline",
    "devops",
    "documentation",
    "customer-facing",
  ];

  const dimensionScores = dimensions.map((dim) => scoreDimension(dim, input));

  const scoreMap = Object.fromEntries(
    dimensionScores.map((ds) => [ds.dimension, ds.score]),
  ) as Record<Dimension, number>;

  const composite = weightedAverage(scoreMap);

  return {
    composite,
    grade: scoreToGrade(composite),
    dimensions: dimensionScores,
  };
}
